'use client'

import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { logout } from '@/lib/api'
import { useDashboardData } from '@/hooks/useDashboardData'
import {
  LayoutDashboard, BarChart3, Truck, Award, Bot, Euro, NotebookPen,
  Settings, LogOut, ChevronLeft, ChevronRight, Moon, Sun
} from 'lucide-react'
import { useState, useEffect } from 'react'

// Clients hidden from nav 2026-10-01 per request -- page is untouched at
// /dashboard/clients, just not linked. Re-add the entry below to revive it.
const NAV_MAIN = [
  { href: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { href: '/dashboard/notes', label: 'Meeting Notes', icon: NotebookPen },
  { href: '/dashboard/schedule-bot', label: 'Schedule Bot', icon: Bot },
  { href: '/dashboard/delivery', label: 'Delivery', icon: Truck },
]

const NAV_ANALYSIS = [
  { href: '/dashboard/kpis', label: 'KPIs', icon: BarChart3 },
  { href: '/dashboard/financials', label: 'Financials', icon: Euro },
  { href: '/dashboard/scorecard', label: 'Scorecard', icon: Award },
]

export default function Sidebar() {
  const pathname = usePathname()
  const { currentUser } = useDashboardData()
  const [collapsed, setCollapsed] = useState(false)
  const [dark, setDark] = useState(false)

  useEffect(() => {
    const saved = localStorage.getItem('remangu_theme')
    const isDark = saved === 'dark' || (!saved && window.matchMedia('(prefers-color-scheme: dark)').matches)
    setDark(isDark)
    document.documentElement.classList.toggle('dark', isDark)
  }, [])

  function toggleTheme() {
    const next = !dark
    setDark(next)
    document.documentElement.classList.toggle('dark', next)
    localStorage.setItem('remangu_theme', next ? 'dark' : 'light')
  }

  function isActive(href: string) {
    if (href === '/dashboard') return pathname === '/dashboard'
    return pathname.startsWith(href)
  }

  const linkClass = (href: string) =>
    `flex items-center gap-3 px-3 py-2 text-base transition-colors ${
      isActive(href)
        ? 'bg-blue-50 text-blue-700 border-l-2 border-blue-600 dark:bg-blue-950/50 dark:text-blue-400'
        : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100 border-l-2 border-transparent dark:hover:text-slate-200 dark:hover:bg-slate-800/50'
    }`

  return (
    <aside className={`fixed inset-y-0 left-0 z-40 flex flex-col bg-white border-r border-slate-200 dark:bg-slate-900 dark:border-slate-800 transition-all ${collapsed ? 'w-16' : 'w-60'}`}>
      {/* Logo */}
      <div className="flex items-center justify-between h-16 px-4 border-b border-slate-200 dark:border-slate-800">
        {!collapsed && (
          <Link href="/dashboard" className="font-[family-name:var(--font-display)] text-lg font-light uppercase tracking-tight">
            <span className="text-blue-600 font-semibold">REMANGU</span>
            <span className="text-blue-600">.</span>
          </Link>
        )}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-300"
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </div>

      {/* Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <div className={`px-3 mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-400 ${collapsed ? 'text-center' : ''}`}>
          {!collapsed && 'Main'}
        </div>
        {NAV_MAIN.map(item => (
          <Link key={item.href} href={item.href} className={linkClass(item.href)} title={item.label}>
            <item.icon size={18} className="shrink-0" />
            {!collapsed && <span>{item.label}</span>}
          </Link>
        ))}

        <div className={`px-3 mt-6 mb-2 text-xs font-semibold uppercase tracking-[0.12em] text-slate-400 ${collapsed ? 'text-center' : ''}`}>
          {!collapsed && 'Analysis'}
        </div>
        {NAV_ANALYSIS.map(item => (
          <Link key={item.href} href={item.href} className={linkClass(item.href)} title={item.label}>
            <item.icon size={18} className="shrink-0" />
            {!collapsed && <span>{item.label}</span>}
          </Link>
        ))}
      </nav>

      {/* Bottom section */}
      <div className="border-t border-slate-200 dark:border-slate-800 py-3">
        <button onClick={toggleTheme} className="flex items-center gap-3 px-3 py-2 text-base text-slate-500 hover:text-slate-900 hover:bg-slate-100 dark:hover:text-slate-200 dark:hover:bg-slate-800/50 border-l-2 border-transparent w-full" title="Toggle theme">
          {dark ? <Sun size={18} className="shrink-0" /> : <Moon size={18} className="shrink-0" />}
          {!collapsed && <span>{dark ? 'Light Mode' : 'Dark Mode'}</span>}
        </button>
        <Link href="/dashboard/settings" className={linkClass('/dashboard/settings')} title="Settings">
          <Settings size={18} className="shrink-0" />
          {!collapsed && <span>Settings</span>}
        </Link>

        {currentUser && (
          <div className={`px-3 mt-3 ${collapsed ? 'text-center' : ''}`}>
            {!collapsed && (
              <div className="text-xs text-slate-500 mb-1 truncate">
                {currentUser.name}
                <span className="ml-1 text-xs uppercase text-slate-400">({currentUser.role})</span>
              </div>
            )}
            <button
              onClick={logout}
              className="flex items-center gap-2 text-xs text-slate-400 hover:text-red-500 transition-colors"
              title="Sign out"
            >
              <LogOut size={14} />
              {!collapsed && <span>Sign Out</span>}
            </button>
          </div>
        )}
      </div>
    </aside>
  )
}
